/**
 * Giftship Cart Integration
 * Handles syncing delivery dates from Giftship datepicker to cart line items
 */

if (!customElements.get('giftship-cart-integration')) {
  class GiftshipCartIntegration extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      // Wait for Giftship to load
      document.addEventListener('giftship.loaded', this.onGiftshipLoaded.bind(this));
      
      // Listen for datepicker updates
      document.addEventListener('giftship.datepicker.updated', this.onDatepickerUpdated.bind(this));
      
      // Listen for cart updates to sync date back to datepicker
      document.addEventListener('on:line-item:change', this.onCartUpdated.bind(this));
      
      // Check if Giftship is already loaded
      if (typeof window.GSSDK !== 'undefined') {
        this.onGiftshipLoaded();
      }
    }

    /**
     * Called when Giftship is loaded
     */
    onGiftshipLoaded() {
      // Giftship is ready
    }

    /**
     * Called when datepicker value is updated
     * @param {CustomEvent} evt - Event containing delivery date details
     */
    async onDatepickerUpdated(evt) {
      // Get the formatted date from the datepicker input
      const datepickerInput = document.querySelector('[name="attributes[_date]"]');
      const deliveryDate = datepickerInput ? datepickerInput.value : (evt.detail?.value || evt.detail?.date);
      
      if (!deliveryDate) return;

      // Update all line items with the new delivery date
      await this.updateAllLineItemsWithDate(deliveryDate);
    }

    /**
     * Updates all line items in the cart with a delivery date
     * @param {string} deliveryDate - The selected delivery date
     */
    async updateAllLineItemsWithDate(deliveryDate) {
      try {
        // Show loading state
        this.setLoadingState(true);

        // Fetch current cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Update each line item sequentially to avoid race conditions
        for (const item of cart.items) {
          await this.updateLineItemWithDate(item.key, deliveryDate);
        }
      } catch (error) {
        this.showError('Failed to update delivery date. Please try again.');
      } finally {
        this.setLoadingState(false);
      }
    }

    /**
     * Updates a single line item with delivery date
     * @param {string} key - Line item key
     * @param {string} deliveryDate - Delivery date
     */
    async updateLineItemWithDate(key, deliveryDate) {
      const requestBody = {
        id: key,
        properties: {
          '_date': deliveryDate
        }
      };
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.description || data.message || 'Failed to update cart');
      }

      return response.json();
    }

    /**
     * Called when cart is updated
     * @param {CustomEvent} evt - Event containing cart update details
     */
    onCartUpdated(evt) {
      // No action needed - datepicker stays intact
    }

    /**
     * Sets loading state
     * @param {boolean} isLoading - Whether loading is active
     */
    setLoadingState(isLoading) {
      const checkoutButton = document.querySelector('button[name="checkout"]');
      if (checkoutButton) {
        checkoutButton.disabled = isLoading;
        if (isLoading) {
          checkoutButton.classList.add('is-loading');
        } else {
          checkoutButton.classList.remove('is-loading');
        }
      }
    }

    /**
     * Shows error message
     * @param {string} message - Error message to display
     */
    showError(message) {
      const errorContainer = document.getElementById('cart-errors');
      if (errorContainer) {
        errorContainer.textContent = message;
        errorContainer.hidden = false;
        
        setTimeout(() => {
          errorContainer.hidden = true;
        }, 5000);
      }
    }
  }

  customElements.define('giftship-cart-integration', GiftshipCartIntegration);
}